import type { FC, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { formatNumber, TWO_DECIMAL_DIGITS } from '@wix/da-shared-react/pkg/utils/formatting';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import Flex from '@wix/da-ds/pkg/Flex';
import DollarSign from '@wix/da-ds/pkg/Icons/16x16/DollarSign';
import ArrowRight from '@wix/da-ds/pkg/Icons/24x24/ArrowRight';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';

import s from './WithdrawalCalculator.scss';

export interface Props {
  /** Current withdrawal amount as string to preserve decimal input */
  withdrawAmount: string;
  /** Function called when withdrawal amount changes */
  onAmountChange: (value: string) => void;
  /** Function to calculate fee based on withdrawal amount */
  calculateFee: (amount: number) => number;
  /** Available balance for withdrawal */
  availableBalance: number;
  /** Minimum withdrawal amount (optional) */
  minimumAmount?: number;
  /** Whether the current amount is valid */
  isValidAmount?: boolean;
  /** Whether to autofocus the input */
  autoFocus?: boolean;
  /** Additional CSS class name */
  className?: string;
}

const WithdrawalCalculator: FC<Props> = ({
  withdrawAmount,
  onAmountChange,
  calculateFee,
  availableBalance,
  minimumAmount,
  isValidAmount = true,
  autoFocus = false,
  className,
}) => {
  const { t } = useTranslation();

  // Parse string to number for calculations
  const amountNumber = parseFloat(withdrawAmount) || 0;
  const feeAmount = calculateFee(amountNumber);
  const finalAmount = Math.max(0, amountNumber - feeAmount);

  // Use the string directly to preserve decimal input
  const inputValue = withdrawAmount;

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Normalize commas to dots
    const normalized = value.replace(/,/g, '.');

    // Sanitize to allow only numbers and dots
    const sanitized = normalized.replace(/[^0-9.]/g, '');

    // Limit to 2 decimal places
    const parts = sanitized.split('.');
    let finalValue: string;

    if (parts.length > 2) {
      // If multiple dots, keep only the first one and combine the rest
      const integerPart = parts[0];
      const decimalPart = parts.slice(1).join('');
      finalValue = `${integerPart}.${decimalPart.slice(0, 2)}`;
    } else if (parts.length === 2 && parts[1].length > 2) {
      // If already has a dot, limit decimal part to 2 digits
      finalValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
    } else {
      finalValue = sanitized;
    }

    onAmountChange(finalValue);
  };

  const getDetailsText = () => {
    // Check if amount is too low
    if (minimumAmount !== undefined && amountNumber > 0 && amountNumber < minimumAmount) {
      return t('studio.earnings.withdraw.amount.error.minimum', {
        minimum: formatNumber(minimumAmount, {
          ...TWO_DECIMAL_DIGITS,
          useGrouping: true,
        }),
      });
    }
    return t('studio.earnings.withdrawalCalculator.available', {
      available: formatNumber(availableBalance, {
        ...TWO_DECIMAL_DIGITS,
        useGrouping: true,
      }),
      minimum: parseInt(String(minimumAmount), 10),
    });
  };

  return (
    <div className={cn(s['root'], className)}>
      <Flex gap={8} alignItems="center">
        <div className={s['input-container']}>
          <TextInput
            id="withdraw-amount"
            label={t('studio.earnings.withdrawalCalculator.amountToWithdraw')}
            value={inputValue}
            onChange={handleAmountChange}
            autoComplete="off"
            placeholder="0.00"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]{0,2}"
            min={minimumAmount}
            max={availableBalance}
            prefix={<DollarSign size={IconSize.SMALLER} />}
            postfix="USD"
            details={getDetailsText()}
            validationResult={isValidAmount ? undefined : 'error'}
            wrapperClassName={s['input-wrapper']}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
          />
        </div>

        <div className={s['arrow-container']}>
          <ArrowRight size={IconSize.MEDIUM} />
        </div>

        <div className={s['input-container']}>
          <TextInput
            id="final-amount"
            label={t('studio.earnings.withdrawalCalculator.amountAfterFee')}
            value={finalAmount.toFixed(2)}
            readOnly
            prefix={<DollarSign size={IconSize.SMALLER} />}
            postfix="USD"
            wrapperClassName={s['input-wrapper']}
          />
        </div>
      </Flex>
    </div>
  );
};

WithdrawalCalculator.displayName = 'WithdrawalCalculator';

export default WithdrawalCalculator;
